import React, { useEffect, useRef } from "react";

import { useLocalization } from "@fluent/react";
import { Button, IconButton, Row, Spinner } from "@narmi/design_system";
import cc from "classcat";
import { Dialog, useNotificationContext } from "cerulean";

import formatFileSize from "../../formatFileSize";
import { useUploadSession } from "../../uploadSession/useUploadSession";

import Dropzone from "./Dropzone";
import { pickUploadRowError } from "./errorMapping";
import useReceiptUploadConfig from "./useReceiptUploadConfig";
import styles from "./UploadReceiptDialog.module.scss";

import type { UploadItem } from "../../uploadSession/uploadSessionReducer";

type UploadReceiptDialogProps = {
  isOpen: boolean;
  onClose: () => void;
};

type UploadRowProps = {
  item: UploadItem;
  maxSizeBytes: number;
  onCancel: (id: string) => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
};

const UploadRow = ({
  item,
  maxSizeBytes,
  onCancel,
  onRemove,
  onRetry,
}: UploadRowProps) => {
  const { l10n } = useLocalization();
  const fileName = item.file.name;

  let icon: React.ReactNode;
  let message: string;
  let actions: React.ReactNode = null;

  const removeButton = (
    <IconButton
      name="x"
      kind="plain"
      label={l10n.getString("receipts-upload-action-remove-file", {
        fileName,
      })}
      onClick={() => onRemove(item.id)}
      testId={`upload-row-remove-${item.id}`}
    />
  );

  switch (item.status) {
    case "queued":
    case "uploading":
      icon = <Spinner size={24} strokeWidth={2} color="var(--theme-primary)" />;
      message = l10n.getString("receipts-upload-uploading-label");
      actions = (
        <IconButton
          name="x"
          kind="plain"
          label={l10n.getString("receipts-upload-action-cancel-file", {
            fileName,
          })}
          onClick={() => onCancel(item.id)}
          testId={`upload-row-cancel-${item.id}`}
        />
      );
      break;
    case "scanning":
      icon = <Spinner size={24} strokeWidth={2} color="var(--theme-primary)" />;
      message = l10n.getString("receipts-upload-row-scanning");
      actions = (
        <IconButton
          name="x"
          kind="plain"
          label={l10n.getString("receipts-upload-action-remove-file", {
            fileName,
          })}
          onClick={() => onCancel(item.id)}
          testId={`upload-row-cancel-${item.id}`}
        />
      );
      break;
    case "added_pending":
    case "added_unmatched":
      icon = (
        <span className={styles["icon-success"]} aria-hidden="true">
          <span className="narmi-icon-check" />
        </span>
      );
      message = l10n.getString(
        item.status === "added_pending"
          ? "receipts-upload-row-added-pending"
          : "receipts-upload-row-added-unmatched"
      );
      actions = removeButton;
      break;
    case "stalled":
      icon = <span className="narmi-icon-clock" aria-hidden="true" />;
      message = l10n.getString("receipts-upload-row-stalled");
      actions = removeButton;
      break;
    case "failed": {
      const { ftlId, vars } = pickUploadRowError(item.errorCode, {
        fileName,
        maxSizeBytes,
        retryAfterSeconds: item.retryAfterSeconds,
      });
      icon = <span className="narmi-icon-alert-circle" aria-hidden="true" />;
      message = l10n.getString(ftlId, vars);
      actions = (
        <>
          {item.retryable && (
            <Button
              kind="secondary"
              size="xs"
              label={l10n.getString("receipts-upload-action-retry")}
              onClick={() => onRetry(item.id)}
              testId={`upload-row-retry-${item.id}`}
            />
          )}
          {removeButton}
        </>
      );
      break;
    }
    default:
      return null;
  }

  return (
    <li
      className={cc([
        styles.row,
        { [styles["row-failed"]]: item.status === "failed" },
      ])}
      data-testid={`upload-row-${item.id}`}
      data-status={item.status}
    >
      <div className={styles["row-icon"]}>{icon}</div>
      <div className={styles["row-text"]}>
        <div className={styles["row-title"]}>
          <span className={styles["row-name"]}>{fileName}</span>
          <span className={styles["row-size"]}>
            {formatFileSize(item.file.size)}
          </span>
        </div>
        <div className={styles["row-message"]} role="status">
          {message}
        </div>
      </div>
      {actions && <div className={styles["row-actions"]}>{actions}</div>}
    </li>
  );
};

const UploadReceiptDialog = ({ isOpen, onClose }: UploadReceiptDialogProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const session = useUploadSession();
  const { status: configStatus, config } = useReceiptUploadConfig(isOpen);
  const configErrorHandledRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      configErrorHandledRef.current = false;
      return;
    }
    if (configStatus !== "error") return;
    if (configErrorHandledRef.current) return;
    configErrorHandledRef.current = true;
    sendNotification({
      type: "negative",
      text: l10n.getString("receipts-upload-config-fetch-error"),
    });
    onClose();
  }, [isOpen, configStatus, l10n, onClose, sendNotification]);

  return (
    <Dialog
      isOpen={isOpen}
      title={l10n.getString("receipts-upload-dialog-title")}
      headerStyle="bordered"
      width="589px"
      onUserDismiss={onClose}
      testId="upload-receipt-dialog"
      footer={
        <Row justifyContent="end" alignItems="center">
          <Row.Item shrink>
            <Button
              kind="primary"
              label={l10n.getString("receipts-upload-action-close")}
              onClick={onClose}
              testId="upload-receipt-close"
            />
          </Row.Item>
        </Row>
      }
    >
      <p className="margin--top--none margin--bottom--m">
        {l10n.getString("receipts-upload-dialog-intro")}
      </p>
      <Dropzone
        multiple
        onFilesSelected={session.add}
        disabled={!session.isReady}
        acceptMimeTypes={config?.allowed_mime_types}
      />
      {session.items.length > 0 && (
        <ul className={styles.rows} data-testid="upload-receipt-rows">
          {session.items.map((item) => (
            <UploadRow
              key={item.id}
              item={item}
              maxSizeBytes={config?.max_size_bytes ?? 0}
              onCancel={session.cancel}
              onRemove={session.remove}
              onRetry={session.retry}
            />
          ))}
        </ul>
      )}
    </Dialog>
  );
};

export default UploadReceiptDialog;
